import {Modal} from 'antd';
import {useEffect, useRef} from 'react';

import {Spinner} from '../../../layout/spinner/spinner';
import {Locale} from '../../../provider/locale/locale';
import {useModal} from '../../../provider/modal/modal-hook';
import {addCompaniesApi} from '../../../service/feature-companies/feature-companies-api';
import {postCompanySelectorCompanySchema} from '../../../service/feature-companies/feature-companies-api-type';
import {CompaniesSelectorFeatureEnum} from '../../../service/feature-companies/feature-companies-const';
import {useCompaniesSelector} from '../../../service/feature-companies/feature-companies-hook';
import {PostSelectorCompanyType} from '../../../service/feature-companies/feature-companies-type';
import {usePromotion, useUpdatePromotion} from '../../../service/promotions/promotions-hook';
import {CommonPromotionStatusEnum, PromotionFormType} from '../../../service/promotions/promotions-type';
import {noop} from '../../../util/function';

import {PromotionForm} from './promotion-form';
import {
    getPromotionFormInitialValues,
    preparePromotionRequestData,
    PROMOTION_FORM_MODAL_WIDTH,
} from './promotion-form-helper';

type PropsType = {
    promotionId: number;
    setIsOpen: (isOpen: boolean) => void;
    onUpdateSuccess?: VoidFunction;
};

export function EditPromotionFormModal({promotionId, setIsOpen, onUpdateSuccess}: PropsType): JSX.Element {
    const {data: promotion, isFetching} = usePromotion(promotionId, true);
    const {mutateAsync: updatePromotion} = useUpdatePromotion(promotionId);
    const {modal} = useModal();

    const companiesSelector = useCompaniesSelector<PostSelectorCompanyType>({
        feature: CompaniesSelectorFeatureEnum.Posts,
        companySchema: postCompanySelectorCompanySchema,
        resourceId: null,
        initialCompaniesCount: promotion?.companiesIds.length ?? 0,
    });

    const {initializeSelector, selectorId, setCompaniesSelectorFilter} = companiesSelector;

    useEffect(() => {
        initializeSelector();
    }, [initializeSelector]);

    // Seed the fresh posts selector with the promotion's existing companies, then reload to reflect them.
    const seededRef = useRef<boolean>(false);

    useEffect(() => {
        if (!promotion || !selectorId || seededRef.current) {
            return;
        }

        seededRef.current = true;

        if (promotion.companiesIds.length > 0) {
            addCompaniesApi({
                selectorId,
                body: {ids: promotion.companiesIds},
                feature: CompaniesSelectorFeatureEnum.Posts,
            })
                .then(() => setCompaniesSelectorFilter((previousFilter) => ({...previousFilter})))
                .catch(noop);
        }
    }, [promotion, selectorId, setCompaniesSelectorFilter]);

    async function handleSubmit(values: PromotionFormType, status: CommonPromotionStatusEnum): Promise<void> {
        await updatePromotion(preparePromotionRequestData(values, selectorId, status));
        onUpdateSuccess?.();
    }

    function handleCancel(): void {
        modal.confirm({
            maskClosable: true,
            title: <Locale stringKey="PROMOTIONS__EDIT__CONFIRM_CLOSE__TITLE" />,
            content: <Locale stringKey="PROMOTIONS__CONFIRM_CLOSE__CONTENT" />,
            okText: <Locale stringKey="PROMOTIONS__EDIT__CONFIRM_CLOSE__CONFIRM" />,
            cancelText: <Locale stringKey="POPUP__BUTTON__CANCEL" />,
            okButtonProps: {danger: true, type: 'primary'},
            onOk: () => setIsOpen(false),
        });
    }

    return (
        <Modal
            centered
            destroyOnClose
            footer={null}
            onCancel={handleCancel}
            open
            title={<Locale stringKey="PROMOTIONS__EDIT__TITLE" />}
            width={PROMOTION_FORM_MODAL_WIDTH}
        >
            {isFetching || !promotion ? (
                <Spinner />
            ) : (
                <PromotionForm
                    closeModal={() => setIsOpen(false)}
                    companiesSelector={companiesSelector}
                    initialValues={getPromotionFormInitialValues(promotion)}
                    mode="edit"
                    onCancel={handleCancel}
                    onSubmit={handleSubmit}
                />
            )}
        </Modal>
    );
}
